import exifr from "exifr";

/**
 * Read image dimensions and EXIF metadata from a File object.
 * Returns a metadata object compatible with Immersive3dTourPanorama.
 *
 * @param {File} file
 * @returns {Promise<{valid: boolean, errors: string[], metadata: Object}>}
 */
export function validateAndExtractMetadata(file) {
  return Promise.all([getImageDimensions(file), getExifData(file)]).then(([dimensions, exifData]) => {
    const { width, height } = dimensions;
    const equirectangular = width === height * 2;

    const errors = [];

    if (!equirectangular) {
      errors.push(
        `Image ${file.name} does not seem to be equirectangular. Please check to make sure the image width is exactly 2 times the height.`,
      );
    }

    return {
      valid: errors.length === 0,
      errors,
      metadata: {
        exifReadable: exifData.exifReadable,
        equirectangular,
        dateTimeOriginal: exifData.dateTimeOriginal,
        width,
        height,
      },
    };
  });
}

/**
 * Get image dimensions using an Image element.
 *
 * @param {File} file
 * @returns {Promise<{width: number, height: number}>}
 */
function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not load image: ${file.name}`));
    };

    img.src = url;
  });
}

/**
 * Extract EXIF data from a File using exifr.
 *
 * @param {File} file
 * @returns {Promise<{exifReadable: boolean, dateTimeOriginal: number|false}>}
 */
function getExifData(file) {
  // Don't use `pick: ["DateTimeOriginal"]`: exifr then returns undefined when that single tag is absent,
  // which would flag images that do have EXIF (just no capture date) as unreadable.
  return exifr
    .parse(file, {
      ifd0: true,
      exif: true,
      gps: false,
      interop: false,
      ifd1: false,
      xmp: false,
      icc: false,
      iptc: false,
    })
    .then(exif => {
      if (!exif) {
        return { exifReadable: false, dateTimeOriginal: false };
      }

      let dateTimeOriginal = false;

      if (exif.DateTimeOriginal) {
        const date = new Date(exif.DateTimeOriginal);
        dateTimeOriginal = Math.floor(date.getTime() / 1000);
      }

      return { exifReadable: true, dateTimeOriginal };
    })
    .catch(() => {
      return { exifReadable: false, dateTimeOriginal: false };
    });
}
